import React from "react";
import { useOutletContext } from "react-router-dom";
import { Mail, Phone, MapPin, Clock, ArrowUpRight } from "lucide-react";

export default function ContactPage() {
  const { content } = useOutletContext() || {};
  const c = content?.contact || {
    email: "info@creativetribe.online",
    phone: "+1-647-569-782",
    address: "Toronto, Ontario, Canada",
    hours: "Mon – Fri · 9:00 AM – 6:00 PM EST",
  };

  const telHref = `tel:${(c.phone || "").replace(/[^+\d]/g, "")}`;

  return (
    <div data-testid="contact-page">
      <section className="relative overflow-hidden px-6 pb-10 pt-20 md:pt-28">
        <div className="absolute inset-0 radial-glow" />
        <div className="relative mx-auto max-w-7xl">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-fuchsia-400">Contact</p>
          <h1 className="mt-4 text-5xl font-bold tracking-tighter text-white md:text-7xl">
            Let's start a <span className="gradient-text">conversation.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-white/70">
            Reach out by email or phone — we typically respond within one business day.
          </p>
        </div>
      </section>

      <section className="px-6 py-14">
        <div className="mx-auto grid max-w-7xl gap-5 md:grid-cols-2">
          <a
            href={`mailto:${c.email}`}
            className="group relative overflow-hidden rounded-3xl border border-white/10 bg-[#131B2F] p-10 hover-lift"
            data-testid="contact-email-card"
          >
            <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-fuchsia-500/10 blur-3xl opacity-0 transition-opacity group-hover:opacity-100" />
            <div className="relative">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-xl border border-white/10 bg-white/5">
                <Mail size={26} className="text-fuchsia-400" />
              </div>
              <p className="mt-8 text-xs uppercase tracking-[0.25em] text-white/40">Email us</p>
              <div className="mt-2 text-3xl font-semibold tracking-tight text-white md:text-4xl" data-testid="contact-email">
                {c.email}
              </div>
              <div className="mt-6 inline-flex items-center gap-2 text-sm text-white/60 group-hover:text-white">
                Open mail <ArrowUpRight size={14} />
              </div>
            </div>
          </a>

          <a
            href={telHref}
            className="group relative overflow-hidden rounded-3xl border border-white/10 bg-[#131B2F] p-10 hover-lift"
            data-testid="contact-phone-card"
          >
            <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-orange-500/10 blur-3xl opacity-0 transition-opacity group-hover:opacity-100" />
            <div className="relative">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-xl border border-white/10 bg-white/5">
                <Phone size={26} className="text-orange-400" />
              </div>
              <p className="mt-8 text-xs uppercase tracking-[0.25em] text-white/40">Call us</p>
              <div className="mt-2 text-3xl font-semibold tracking-tight text-white md:text-4xl" data-testid="contact-phone">
                {c.phone}
              </div>
              <div className="mt-6 inline-flex items-center gap-2 text-sm text-white/60 group-hover:text-white">
                Start a call <ArrowUpRight size={14} />
              </div>
            </div>
          </a>
        </div>
      </section>

      <section className="px-6 pb-28">
        <div className="mx-auto grid max-w-7xl gap-5 md:grid-cols-2">
          <div className="rounded-2xl border border-white/10 bg-[#131B2F] p-8" data-testid="contact-address">
            <div className="flex items-center gap-3">
              <MapPin size={18} className="text-fuchsia-400" />
              <span className="text-xs uppercase tracking-[0.25em] text-white/40">Based in</span>
            </div>
            <p className="mt-3 text-lg text-white/90">{c.address}</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#131B2F] p-8" data-testid="contact-hours">
            <div className="flex items-center gap-3">
              <Clock size={18} className="text-fuchsia-400" />
              <span className="text-xs uppercase tracking-[0.25em] text-white/40">Office hours</span>
            </div>
            <p className="mt-3 text-lg text-white/90">{c.hours}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
